import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { getDisplayErrorMessage } from '@/lib/api';
import { fetchBreakerDone, pressBreakerDone, type BreakerDoneView } from '@/lib/remediation';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';
import styles from './MagicLinkVerifyPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * The Done link in the remediation ladder's building ask (Warren, 28 Oct 2026, evening addendum item 1, step d):
 * /breaker-done?token=..., no sign-in (hoisted above the auth guard like the alert choice page). Opening the page only
 * reads; the one button records Done (ruling 10, 29 Oct: mail scanners open every link in an email, so a GET never
 * records anything). The platform then watches for the charger to reconnect and emails whether it worked.
 */
export function BreakerDonePage({ onNavigate }: Props) {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [view, setView] = useState<BreakerDoneView | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError('This link is incomplete. Open it from the email again.'); return; }
    fetchBreakerDone(token)
      .then(setView)
      .catch(async (err: unknown) => setError(await getDisplayErrorMessage(err, 'This link is no longer valid.')));
  }, [token]);

  const press = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try { setView(await pressBreakerDone(token)); } catch (err) { setError(await getDisplayErrorMessage(err, 'Could not record it. Try again.')); } finally { setBusy(false); }
  };

  const names = view ? view.chargers.map((c) => (c.label === c.identity ? c.label : `${c.label} (${c.identity})`)).join(', ') : '';
  return (
    <AuthLayout title={view ? view.location_name : 'Charger reset'} subtitle="Cycling the breaker">
      {!view && !error && <div className={styles.spinnerWrap}><Loader2 size={36} className={styles.spinner} /></div>}
      {error && (
        <div className={styles.errorStack}>
          <div className={formStyles.serverError} role="alert">
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {error}
          </div>
          {!view && <button type="button" className={formStyles.btnSecondary} onClick={() => onNavigate('/login')}>Go to sign in</button>}
        </div>
      )}
      {view && (
        <div className={styles.errorStack}>
          <p>{view.chargers.length === 1 ? 'Charger' : 'Chargers'}: <strong>{names}</strong></p>
          {view.state === 'open' && <>
            <p><strong>{view.step}</strong></p>
            <button type="button" className={formStyles.submitBtn} disabled={busy} onClick={() => { void press(); }}>
              {busy ? 'Recording...' : 'Done'}
            </button>
          </>}
          {view.state === 'done' && <p role="status"><CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Thank you. We are watching for it to reconnect and will email you whether it worked.</p>}
          {view.state === 'back_online' && <p role="status"><CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            It is working again. Nothing more to do, thank you.</p>}
          {view.state === 'closed' && <p role="status">This request is closed. If a charger still needs help, reply to the email and EV Initiative will help.</p>}
        </div>
      )}
    </AuthLayout>
  );
}

export default BreakerDonePage;
